import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Learners: React.FC = () => {
  const features = [
    {
      icon: 'school',
      title: 'Expert Instructors',
      description: 'Learn from industry professionals and certified experts in their fields.'
    },
    {
      icon: 'schedule',
      title: 'Flexible Learning',
      description: 'Study at your own pace with lifetime access to course materials.'
    },
    {
      icon: 'verified',
      title: 'Certified Courses',
      description: 'Earn certificates upon completion to showcase your new skills.'
    },
    {
      icon: 'devices',
      title: 'Multi-Platform',
      description: 'Access your courses on any device, anywhere, anytime.'
    }
  ];

  const categories = [
    { name: 'Technology', icon: 'computer', color: 'from-blue-500 to-cyan-500', count: 150 },
    { name: 'Business', icon: 'business', color: 'from-green-500 to-emerald-500', count: 120 },
    { name: 'Astrology', icon: 'orbit', color: 'from-purple-500 to-pink-500', count: 80 },
    { name: 'Marketing', icon: 'trending_up', color: 'from-orange-500 to-red-500', count: 95 },
    { name: 'Health', icon: 'favorite', color: 'from-red-500 to-pink-500', count: 60 },
    { name: 'Language', icon: 'language', color: 'from-indigo-500 to-purple-500', count: 45 }
  ];

  const learningBenefits = [
    {
      icon: '🎯',
      title: 'Personalized Learning Paths',
      description: 'Get customized learning recommendations based on your goals, experience level, and interests.'
    },
    {
      icon: '👥',
      title: '1:1 Mentorship',
      description: 'Book personalized consultations with course creators for direct guidance and mentorship.'
    },
    {
      icon: '📱',
      title: 'Mobile-First Experience',
      description: 'Learn on-the-go with our mobile-optimized platform that works seamlessly across all devices.'
    },
    {
      icon: '🏆',
      title: 'Achievement Tracking',
      description: 'Monitor your progress, earn badges, and celebrate milestones as you advance through courses.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/10 via-transparent to-accent-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-7xl font-display font-bold text-gray-900 mb-4 sm:text-5xl">
                Unlock Your Potential with{' '}
                <span className="text-gradient">Gyaanly</span>
              </h1>
              <p className="text-lg md:text-2xl text-gray-600 mb-6 max-w-3xl mx-auto leading-relaxed sm:text-xl">
                Transform your skills with expert-led courses. Learn from industry professionals 
                and unlock your potential with our comprehensive learning platform.
              </p>
            </motion.div>

            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
            >
              <Link to="/courses" className="flex btn-primary text-lg px-8 py-4 items-center justify-center">
                <span className="material-symbols-rounded mr-2">search</span>
                Explore Courses
              </Link>
              <Link to="/register" className="flex btn-secondary text-lg px-8 py-4 items-center justify-center">
                <span className="material-symbols-rounded mr-2">person_add</span>
                Start Learning
              </Link>
            </motion.div>

 */}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-display font-bold text-gradient mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Learning Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-display font-bold text-gray-900 mb-4 sm:text-4xl">
              Why Learn with <span className="text-gradient">Gyaanly</span>?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto sm:text-xl">
              We provide the best learning experience with cutting-edge technology and expert instructors.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {learningBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card-hover text-center group"
              >
                <div className="w-16 h-16 icon-primary flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">{benefit.icon}</span>
                </div>
                <h3 className="text-xl font-display font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-display font-bold text-gray-900 mb-4 sm:text-4xl">
              Learning Features
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto sm:text-xl">
              Everything you need for an effective and enjoyable learning experience.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card-hover text-center group"
              >
                <div className="w-16 h-16 icon-primary flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <span className="material-symbols-rounded text-white text-2xl">{feature.icon}</span>
                </div>
                <h3 className="text-xl font-display font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Paths Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-display font-bold text-gray-900 mb-4 sm:text-4xl">
              Structured Learning Paths
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto sm:text-xl">
              Follow curated learning paths designed by industry experts to achieve your career goals efficiently.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="card-hover p-6 border-l-4 border-primary-500"
            >
              <h3 className="text-xl font-display font-semibold text-gray-900 mb-3">Beginner to Advanced</h3>
              <p className="text-gray-600 mb-4">Start from the basics and progress through intermediate to advanced concepts with structured progression.</p>
              <div className="flex items-center text-primary-600">
                <span className="material-symbols-rounded mr-2">trending_up</span>
                <span className="text-sm font-medium">Progressive Learning</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="card-hover p-6 border-l-4 border-accent-500"
            >
              <h3 className="text-xl font-display font-semibold text-gray-900 mb-3">Project-Based Learning</h3>
              <p className="text-gray-600 mb-4">Apply your knowledge through hands-on projects that build a portfolio of real-world work.</p>
              <div className="flex items-center text-accent-600">
                <span className="material-symbols-rounded mr-2">build</span>
                <span className="text-sm font-medium">Practical Application</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="card-hover p-6 border-l-4 border-green-500"
            >
              <h3 className="text-xl font-display font-semibold text-gray-900 mb-3">Certification Tracks</h3>
              <p className="text-gray-600 mb-4">Earn industry-recognized certifications that validate your skills and boost your career prospects.</p>
              <div className="flex items-center text-green-600">
                <span className="material-symbols-rounded mr-2">verified</span>
                <span className="text-sm font-medium">Industry Recognition</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-display font-bold text-gray-900 mb-4 sm:text-4xl">
              Popular Learning Categories
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto sm:text-xl">
              Explore courses across various domains and find your passion.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card-hover group cursor-pointer flex flex-col items-center justify-center"
              >
                <div className="items-center space-x-4 justify-center">
                  <div className={`w-12 h-12 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <span className="material-symbols-rounded text-white text-xl">{category.icon}</span>
                  </div>
                  
                </div>
                <div className="mt-2">
                    <h3 className="text-lg font-display font-semibold text-gray-900 group-hover:text-primary-600 transition-colors duration-200">
                      {category.name}
                    </h3>
                  </div>
              </motion.div>
            ))}
          </div>


        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-4 sm:text-4xl">
              Ready to Start Your Learning Journey?
            </h2>
            <p className="text-lg text-primary-100 mb-6 max-w-3xl mx-auto sm:text-xl">
              Join thousands of learners who have transformed their careers with Gyaanly.
            </p>

          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Learners;
